// @workflow assistant.feedback
/**
 * Reply versions (Try again keeps every version) and reply feedback (thumbs up / down with an
 * optional reason). Contract: .claude/features/AVA_ASSISTANT_EVENT_CONTRACT.md, "Reply feedback
 * and Try again". Showcase: src/assets/showcase/ai-chat-client-feedback-showcase.html (variant A).
 * Every function is immutable. No Vue or path-alias imports, so node --test can load it directly.
 */
import { ROLE } from './events.js';

export const RATING = Object.freeze({ UP: 'up', DOWN: 'down' });

export const FEEDBACK_REASON = Object.freeze({
  WRONG_DATA: 'wrong_data',
  NOT_WHAT_I_ASKED: 'not_what_i_asked',
  TOO_SLOW: 'too_slow',
  OTHER: 'other',
});

/** What the "What went wrong?" chips say, in order. */
export const FEEDBACK_REASON_LABELS = Object.freeze([
  [FEEDBACK_REASON.WRONG_DATA, 'Wrong data'],
  [FEEDBACK_REASON.NOT_WHAT_I_ASKED, "Didn't do what I asked"],
  [FEEDBACK_REASON.TOO_SLOW, 'Too slow'],
  [FEEDBACK_REASON.OTHER, 'Something else'],
]);

export const FEEDBACK_COMMENT_MAX = 1000;

const RATINGS = Object.values(RATING);
const REASONS = Object.values(FEEDBACK_REASON);

const rootOf = (m) => m.versionOf || m.id;

/** Every version of the reply `messageId` belongs to, oldest first ([] if there is none). */
export function versionsOf(c, messageId) {
  const message = c.messages.find((m) => m.id === messageId);
  if (!message) return [];
  const root = rootOf(message);
  return c.messages.filter((m) => m.role === ROLE.ASSISTANT && rootOf(m) === root);
}

function shownVersionId(c, root) {
  const picked = c.selectedVersions && c.selectedVersions[root];
  if (picked) return picked;
  const all = c.messages.filter((m) => m.role === ROLE.ASSISTANT && rootOf(m) === root);
  return all[all.length - 1].id;
}

/** The thread as shown: one version per reply (the picked one, else the newest). */
export function visibleMessages(c) {
  return c.messages.filter((m) => m.role !== ROLE.ASSISTANT || shownVersionId(c, rootOf(m)) === m.id);
}

/** Shows `messageId` instead of the reply's other versions. */
export function selectVersion(c, messageId) {
  const message = c.messages.find((m) => m.id === messageId);
  if (!message) return c;
  return { ...c, selectedVersions: { ...(c.selectedVersions || {}), [rootOf(message)]: messageId } };
}

/**
 * Records the user's rating for one version. Unknown ratings clear it; a reason only goes with a
 * thumbs-down, and a note only with "Something else".
 * @param {{ rating: 'up'|'down'|null, reason?: string, comment?: string }} feedback
 */
export function setFeedback(c, messageId, { rating, reason = null, comment = '' }) {
  const { [messageId]: previous, ...rest } = c.feedback || {};
  if (!RATINGS.includes(rating)) return { ...c, feedback: rest };
  const keptReason = rating === RATING.DOWN && REASONS.includes(reason) ? reason : null;
  const keptComment = keptReason === FEEDBACK_REASON.OTHER ? String(comment || '').trim().slice(0, FEEDBACK_COMMENT_MAX) : '';
  return { ...c, feedback: { ...rest, [messageId]: { rating, reason: keptReason, comment: keptComment } } };
}

/** @returns {{ rating: string, reason: string|null, comment: string }|null} */
export const feedbackFor = (c, messageId) => (c.feedback && c.feedback[messageId]) || null;

/** The user turn a reply (any version) answers: what Try again asks again. */
export function questionFor(c, messageId) {
  const message = c.messages.find((m) => m.id === messageId);
  if (!message) return null;
  const at = c.messages.findIndex((m) => m.id === rootOf(message));
  for (let i = at - 1; i >= 0; i -= 1) {
    if (c.messages[i].role === ROLE.USER) return c.messages[i];
  }
  return null;
}
